import { useState, useEffect, useMemo } from 'react';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Plus, Pencil, Trash2, Search, X, Check } from 'lucide-react';

// Small built-in suggestion list (reused from the platform's known-credential set).
// This is NOT an external catalog and requires no integration; manual entry always works.
const CREDENTIAL_SUGGESTIONS = [
  { name: 'CompTIA Security+', issuer: 'CompTIA', credential_type: 'certification' },
  { name: 'CompTIA Network+', issuer: 'CompTIA', credential_type: 'certification' },
  { name: 'CompTIA A+', issuer: 'CompTIA', credential_type: 'certification' },
  { name: 'CompTIA CySA+', issuer: 'CompTIA', credential_type: 'certification' },
  { name: 'CompTIA PenTest+', issuer: 'CompTIA', credential_type: 'certification' },
  { name: 'CISSP', issuer: '(ISC)\u00b2', credential_type: 'certification' },
  { name: 'CISM', issuer: 'ISACA', credential_type: 'certification' },
  { name: 'CISA', issuer: 'ISACA', credential_type: 'certification' },
  { name: 'Certified Ethical Hacker (CEH)', issuer: 'EC-Council', credential_type: 'certification' },
  { name: 'OSCP', issuer: 'OffSec', credential_type: 'certification' },
  { name: 'GSEC', issuer: 'GIAC', credential_type: 'certification' },
  { name: 'PMP', issuer: 'PMI', credential_type: 'certification' },
  { name: 'CAPM', issuer: 'PMI', credential_type: 'certification' },
  { name: 'PMI-ACP', issuer: 'PMI', credential_type: 'certification' },
  { name: 'AWS Certified Solutions Architect', issuer: 'Amazon Web Services', credential_type: 'certification' },
  { name: 'AWS Certified Cloud Practitioner', issuer: 'Amazon Web Services', credential_type: 'certification' },
  { name: 'Microsoft Certified: Azure Administrator', issuer: 'Microsoft', credential_type: 'certification' },
  { name: 'Cisco CCNA', issuer: 'Cisco', credential_type: 'certification' },
  { name: 'Cisco CCNP', issuer: 'Cisco', credential_type: 'certification' },
  { name: 'Cisco CCIE', issuer: 'Cisco', credential_type: 'certification' },
  { name: 'ITIL Foundation', issuer: 'Axelos', credential_type: 'certification' },
  { name: 'Lean Six Sigma', issuer: '', credential_type: 'certification' },
  { name: 'Commercial Driver\u2019s License (CDL)', issuer: 'State DMV', credential_type: 'license' },
  { name: 'EMT', issuer: 'NREMT', credential_type: 'certification' },
  { name: 'Paramedic', issuer: 'NREMT', credential_type: 'license' },
  { name: 'Registered Nurse (RN)', issuer: 'State Board of Nursing', credential_type: 'license' },
  { name: 'Licensed Practical Nurse (LPN)', issuer: 'State Board of Nursing', credential_type: 'license' },
  { name: 'Certified Nursing Assistant (CNA)', issuer: '', credential_type: 'certification' },
  { name: 'BLS (Basic Life Support)', issuer: 'American Heart Association', credential_type: 'certification' },
  { name: 'ACLS', issuer: 'American Heart Association', credential_type: 'certification' },
  { name: 'CPA', issuer: 'State Board of Accountancy', credential_type: 'license' },
  { name: 'CFA', issuer: 'CFA Institute', credential_type: 'certification' },
  { name: 'FINRA Series 7', issuer: 'FINRA', credential_type: 'license' },
  { name: 'FINRA Series 63', issuer: 'FINRA', credential_type: 'license' },
  { name: 'OSHA 30', issuer: 'OSHA', credential_type: 'certification' },
  { name: 'Professional Engineer (PE)', issuer: 'State Licensing Board', credential_type: 'license' },
];

const TYPE_LABEL = { certification: 'Certification', license: 'License', other: 'Other' };
const MONTHS = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const normKey = (name, issuer) => `${(name || '').trim().toLowerCase()}|${(issuer || '').trim().toLowerCase()}`;

function fmtExpiration(exp) {
  if (!exp) return null;
  const m = /^(\d{4})-(\d{1,2})$/.exec(exp.trim());
  if (m) {
    const mo = parseInt(m[2], 10);
    return mo >= 1 && mo <= 12 ? `${MONTHS[mo]} ${m[1]}` : m[1];
  }
  if (/^\d{4}$/.test(exp.trim())) return exp.trim();
  return exp.trim();
}

const emptyDraft = () => ({ name: '', issuer: '', credential_type: 'certification', expiration_date: '' });

export default function AwardsStep({ resume: resumeData, registerPendingData }) {
  const [credentials, setCredentials] = useState([]);
  const [legacyCerts, setLegacyCerts] = useState([]);
  const [legacyDirty, setLegacyDirty] = useState(false);
  const [additionalText, setAdditionalText] = useState('');
  const [isDirty, setIsDirty] = useState(false);

  // Search + add/edit state
  const [query, setQuery] = useState('');
  const [showSuggest, setShowSuggest] = useState(false);
  const [editIndex, setEditIndex] = useState(null); // null = closed, -1 = adding new, >=0 editing
  const [draft, setDraft] = useState(emptyDraft());
  const [dupError, setDupError] = useState('');

  // Hydrate. Structured professional_credentials and legacy string certifications are
  // kept SEPARATE: structured is the primary editor; legacy strings are shown only as
  // removable "previously saved" chips for entries not already covered by a structured item.
  useEffect(() => {
    if (!resumeData) return;
    setCredentials(Array.isArray(resumeData.professional_credentials) ? resumeData.professional_credentials : []);
    setLegacyCerts(Array.isArray(resumeData.certifications) ? resumeData.certifications.filter((c) => c && c.trim()) : []);
    setLegacyDirty(false);
    setAdditionalText(resumeData.additional_awards_text || '');
    setIsDirty(false);
  }, [resumeData]);

  // Persist civilian recognition + structured credentials via canonical save path.
  // Legacy `certifications` is included ONLY when the user removed a legacy chip, so we
  // never rewrite it (and its scoring hash) unnecessarily.
  useEffect(() => {
    if ((isDirty || legacyDirty) && registerPendingData) {
      registerPendingData({
        additional_awards_text: additionalText || null,
        professional_credentials: credentials.map((c) => ({
          name: (c.name || '').trim(),
          issuer: (c.issuer || '').trim() || null,
          credential_type: c.credential_type || 'certification',
          expiration_date: (c.expiration_date || '').trim() || null,
          source: c.source || 'manual',
          ...(c.source_id ? { source_id: c.source_id } : {}),
        })),
        ...(legacyDirty ? { certifications: legacyCerts } : {}),
      });
    }
  }, [additionalText, credentials, legacyCerts, isDirty, legacyDirty, registerPendingData]);

  // Legacy certs not already represented by an equivalent structured credential (exact
  // normalized name match) — conservative, no fuzzy merging of unrelated credentials.
  const structuredNames = new Set(credentials.map((c) => (c.name || '').trim().toLowerCase().replace(/\s+/g, ' ')));
  const orphanLegacy = legacyCerts.filter((c) => !structuredNames.has(c.trim().toLowerCase().replace(/\s+/g, ' ')));
  const removeLegacyCert = (value) => {
    setLegacyCerts((prev) => prev.filter((c) => c !== value));
    setLegacyDirty(true);
  };

  const suggestions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    const existing = new Set(credentials.map((c) => normKey(c.name, c.issuer)));
    return CREDENTIAL_SUGGESTIONS
      .filter((s) => s.name.toLowerCase().includes(q) || (s.issuer || '').toLowerCase().includes(q))
      .filter((s) => !existing.has(normKey(s.name, s.issuer)))
      .slice(0, 6);
  }, [query, credentials]);

  const isDuplicate = (name, issuer, ignoreIndex = null) => {
    const key = normKey(name, issuer);
    return credentials.some((c, i) => i !== ignoreIndex && normKey(c.name, c.issuer) === key);
  };

  const addCredential = (rec) => {
    if (isDuplicate(rec.name, rec.issuer)) return false;
    setCredentials((prev) => [...prev, rec]);
    setIsDirty(true);
    return true;
  };

  const pickSuggestion = (s) => {
    addCredential({ ...s, expiration_date: '', source: 'suggestion' });
    setQuery('');
    setShowSuggest(false);
  };

  const startManual = () => {
    setDraft({ ...emptyDraft(), name: query.trim() });
    setEditIndex(-1);
    setDupError('');
    setShowSuggest(false);
  };

  const startEdit = (idx) => {
    const c = credentials[idx];
    setDraft({ name: c.name || '', issuer: c.issuer || '', credential_type: c.credential_type || 'certification', expiration_date: c.expiration_date || '' });
    setEditIndex(idx);
    setDupError('');
  };

  const saveDraft = () => {
    const name = (draft.name || '').trim();
    if (!name) return;
    const issuer = (draft.issuer || '').trim();
    if (isDuplicate(name, issuer, editIndex >= 0 ? editIndex : null)) {
      setDupError('This credential is already on your resume.');
      return;
    }
    const rec = {
      name, issuer, credential_type: draft.credential_type || 'certification',
      expiration_date: (draft.expiration_date || '').trim(),
      source: editIndex >= 0 ? (credentials[editIndex].source || 'manual') : 'manual',
      ...(editIndex >= 0 && credentials[editIndex].source_id ? { source_id: credentials[editIndex].source_id } : {}),
    };
    if (editIndex >= 0) {
      setCredentials((prev) => prev.map((c, i) => (i === editIndex ? rec : c)));
    } else {
      setCredentials((prev) => [...prev, rec]);
    }
    setIsDirty(true);
    setEditIndex(null);
    setDraft(emptyDraft());
    setDupError('');
    setQuery('');
  };

  const removeCredential = (idx) => {
    setCredentials((prev) => prev.filter((_, i) => i !== idx));
    setIsDirty(true);
    if (editIndex === idx) { setEditIndex(null); setDraft(emptyDraft()); }
  };

  return (
    <div className="space-y-8" data-testid="awards-step">
      {/* ===== 1. PROFESSIONAL CERTIFICATIONS & LICENSES (structured) ===== */}
      <div data-testid="professional-credentials-section">
        <h3 className="text-lg font-semibold text-slate-900">Professional Certifications &amp; Licenses</h3>
        <p className="text-sm text-slate-500 mt-1">
          Add civilian certifications or professional licenses that may strengthen your resume.
        </p>

        {/* Search / autocomplete */}
        <div className="relative mt-3">
          <div className="relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <Input
              data-testid="credential-search-input"
              value={query}
              onChange={(e) => { setQuery(e.target.value); setShowSuggest(true); }}
              onFocus={() => setShowSuggest(true)}
              onKeyDown={(e) => { if (e.key === 'Enter' && query.trim()) { e.preventDefault(); startManual(); } }}
              className="h-11 pl-9"
              placeholder="Search certifications or licenses..."
            />
          </div>
          {showSuggest && query.trim() && (
            <div className="absolute z-20 mt-1 w-full rounded-lg border border-slate-200 bg-white shadow-lg overflow-hidden" data-testid="credential-suggestions">
              {suggestions.map((s, i) => (
                <button
                  key={i}
                  type="button"
                  data-testid={`credential-suggestion-${i}`}
                  onClick={() => pickSuggestion(s)}
                  className="w-full text-left px-3 py-2 hover:bg-brand-light transition-colors border-b border-slate-100 last:border-b-0"
                >
                  <span className="text-sm font-medium text-slate-800">{s.name}</span>
                  {s.issuer && <span className="text-xs text-slate-500 ml-2">{s.issuer}</span>}
                </button>
              ))}
              <button
                type="button"
                data-testid="credential-add-manually-btn"
                onClick={startManual}
                className="w-full text-left px-3 py-2 text-sm font-medium text-brand hover:bg-brand-light transition-colors flex items-center gap-1.5"
              >
                <Plus className="w-4 h-4" />
                Can&apos;t find it? Add &ldquo;{query.trim()}&rdquo; manually
              </button>
            </div>
          )}
        </div>

        {/* Manual add / edit form */}
        {editIndex !== null && (
          <Card className="p-4 mt-3 border border-brand/40 bg-brand-light/40" data-testid="credential-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <Label className="text-xs font-medium text-slate-700">Name</Label>
                <Input
                  data-testid="credential-name-input"
                  value={draft.name}
                  onChange={(e) => { setDraft({ ...draft, name: e.target.value }); setDupError(''); }}
                  className="h-10 mt-1"
                  placeholder="e.g., CompTIA Security+"
                />
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-700">Issuer <span className="text-slate-400">(optional)</span></Label>
                <Input
                  data-testid="credential-issuer-input"
                  value={draft.issuer}
                  onChange={(e) => setDraft({ ...draft, issuer: e.target.value })}
                  className="h-10 mt-1"
                  placeholder="e.g., CompTIA"
                />
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-700">Type</Label>
                <select
                  data-testid="credential-type-select"
                  value={draft.credential_type}
                  onChange={(e) => setDraft({ ...draft, credential_type: e.target.value })}
                  className="h-10 mt-1 w-full rounded-md border border-slate-200 bg-white px-3 text-sm"
                >
                  <option value="certification">Certification</option>
                  <option value="license">License</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-700">Expiration <span className="text-slate-400">(optional)</span></Label>
                <Input
                  data-testid="credential-expiration-input"
                  type="month"
                  value={draft.expiration_date}
                  onChange={(e) => setDraft({ ...draft, expiration_date: e.target.value })}
                  className="h-10 mt-1"
                />
              </div>
            </div>
            {dupError && <p className="text-xs text-red-600 mt-2" data-testid="credential-dup-error">{dupError}</p>}
            <div className="flex gap-2 mt-3">
              <Button
                data-testid="credential-save-btn"
                onClick={saveDraft}
                disabled={!draft.name.trim()}
                size="sm"
                className="bg-brand text-white hover:bg-brand/90"
              >
                <Check className="w-4 h-4 mr-1" /> {editIndex >= 0 ? 'Update' : 'Add'}
              </Button>
              <Button
                data-testid="credential-cancel-btn"
                onClick={() => { setEditIndex(null); setDraft(emptyDraft()); setDupError(''); }}
                variant="ghost"
                size="sm"
              >
                <X className="w-4 h-4 mr-1" /> Cancel
              </Button>
            </div>
          </Card>
        )}

        {/* Credential cards */}
        {credentials.length > 0 && (
          <div className="mt-3 space-y-2" data-testid="credential-list">
            {credentials.map((c, idx) => {
              const exp = fmtExpiration(c.expiration_date);
              const meta = [c.issuer, TYPE_LABEL[c.credential_type] || 'Certification', exp ? `Expires ${exp}` : null].filter(Boolean);
              return (
                <div
                  key={idx}
                  data-testid={`credential-card-${idx}`}
                  className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white px-3.5 py-2.5"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-900 truncate">{c.name}</p>
                    {meta.length > 0 && <p className="text-xs text-slate-500 truncate">{meta.join(' \u00b7 ')}</p>}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button data-testid={`credential-edit-${idx}`} onClick={() => startEdit(idx)} variant="ghost" size="sm" className="h-8 w-8 p-0 text-slate-500 hover:text-brand">
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button data-testid={`credential-remove-${idx}`} onClick={() => removeCredential(idx)} variant="ghost" size="sm" className="h-8 w-8 p-0 text-slate-400 hover:text-red-600">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Legacy string certifications not represented by a structured credential.
            Removable via the canonical `certifications` save path; data preserved otherwise. */}
        {orphanLegacy.length > 0 && (
          <div className="mt-3" data-testid="legacy-certs">
            <p className="text-xs text-slate-400 mb-1.5">Previously saved</p>
            <div className="flex flex-wrap gap-2">
              {orphanLegacy.map((cert, idx) => (
                <span
                  key={idx}
                  data-testid={`legacy-cert-${idx}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-50 border border-dashed border-slate-300 rounded-md text-sm text-slate-600"
                >
                  {cert}
                  <button
                    onClick={() => removeLegacyCert(cert)}
                    data-testid={`remove-legacy-cert-${idx}`}
                    className="text-slate-400 hover:text-red-600 transition-colors ml-0.5"
                    title="Remove"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          </div>
        )}

        {credentials.length === 0 && orphanLegacy.length === 0 && editIndex === null && (
          <div className="mt-3 rounded-lg border border-dashed border-slate-200 p-4 text-center" data-testid="credentials-empty">
            <p className="text-sm text-slate-500">No certifications or licenses added yet</p>
          </div>
        )}

        {editIndex === null && (
          <Button
            data-testid="credential-add-manual-cta"
            onClick={startManual}
            variant="outline"
            size="sm"
            className="mt-3 border-dashed border-slate-300 text-slate-700 hover:bg-slate-50"
          >
            <Plus className="w-4 h-4 mr-1.5" /> Add manually
          </Button>
        )}
      </div>

      {/* ===== 2. CIVILIAN & PROFESSIONAL RECOGNITION — unchanged free-text ===== */}
      <div className="pt-2 border-t border-slate-200">
        <h3 className="text-lg font-semibold text-slate-900">Civilian &amp; Professional Recognition</h3>
        <p className="text-sm text-slate-500 mt-1">
          Add any civilian or professional awards, honors or recognition (one per line). Optional.
        </p>
        <Textarea
          data-testid="additional-awards-text"
          value={additionalText || ''}
          onChange={(e) => { setAdditionalText(e.target.value); setIsDirty(true); }}
          placeholder="e.g., Employee of the Year 2023, Dean's List, industry recognition…"
          rows={3}
          className="mt-3 text-sm"
        />
      </div>
    </div>
  );
}
